import { useState, useEffect } from 'react'

/**
 * useMediaQuery
 *
 * SSR-safe wrapper around window.matchMedia.
 * Re-renders only when the match result changes (not on every resize).
 *
 * Usage:
 *   const isMobile  = useMediaQuery('(max-width: 639px)')
 *   const isDesktop = useMediaQuery('(min-width: 1024px)')
 *
 * Pre-built breakpoint helpers are exported below — prefer those
 * over raw query strings to keep breakpoints consistent with Tailwind.
 */
export function useMediaQuery(query: string): boolean {
  const getMatch = () =>
    typeof window !== 'undefined'
      ? window.matchMedia(query).matches
      : false

  const [matches, setMatches] = useState(getMatch)

  useEffect(() => {
    const mql = window.matchMedia(query)
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches)

    // Modern API
    mql.addEventListener('change', handler)
    // Sync immediately in case query changed between render and effect
    setMatches(mql.matches)

    return () => mql.removeEventListener('change', handler)
  }, [query])

  return matches
}

// ─── Tailwind-aligned breakpoint helpers ─────────────────────────────────────
// Mirrors Tailwind's default screens: sm=640, md=768, lg=1024, xl=1280

/** true when viewport < 640px  (below Tailwind's `sm`) */
export const useMobile  = () => useMediaQuery('(max-width: 639px)')

/** true when viewport >= 640px (Tailwind's `sm` and up) */
export const useSm      = () => useMediaQuery('(min-width: 640px)')

/** true when viewport >= 768px (Tailwind's `md` and up) */
export const useMd      = () => useMediaQuery('(min-width: 768px)')

/** true when viewport >= 1024px (Tailwind's `lg` and up) */
export const useLg      = () => useMediaQuery('(min-width: 1024px)')

/** true when viewport >= 1280px (Tailwind's `xl` and up) */
export const useXl      = () => useMediaQuery('(min-width: 1280px)')

/** true when user prefers reduced motion */
export const usePrefersReducedMotion = () =>
  useMediaQuery('(prefers-reduced-motion: reduce)')